import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPagination from "@toothfairy/shared-ui/AppPagination";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import benchmarksHelper from "../../../../API/Benchmarks/benchmarksHelper";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import ToothFairyModal from "../../../../Components/ToothFairyComponents/ToothFairyModal";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import BenchmarksSettings from "./BenchmarksSettings";
import BenchmarksSettingsToolbar from "./BenchmarksSettingsToolbar";

const BenchmarksList = ({ isFunctionsLoading }) => {
  const formRef = useRef();
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const { getPlanConfig, getWorkspaceEntities } = useWorkspaces();
  const [displayTokenSection, setDisplayTokenSection] = React.useState(false);
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [showBenchmarkModal, setShowBenchmarkModal] = React.useState(false);
  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const pageSize = 50;
  const [displayedBenchmarks, setDisplayedBenchmarks] = React.useState([]);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { height } = useWindowDimensions();
  const { getActiveWorkspace } = useWorkspaces();
  const [initialMapValues, setInitialMapValues] = useState({
    name: "",
    description: "",
  });
  const _topics = getWorkspaceEntities("topic")?.map((topic) => {
    topic.name = topic["label"];
    topic.category = topic["type"];
    topic.value = topic["id"];
    return { ...topic };
  });
  const columns = [
    {
      title: "ID",
      width: 0.00001,
    },
    {
      title: "Name",
      width: 0.3,
    },
    {
      title: "Description",
    },
    {
      title: "Records",
      width: 0.1,
    },
    {
      title: "Actions",
      isAction: true,
      width: 0.1,
    },
  ];
  const onBenchmarkCreated = useCallback(
    (event) => {
      if (event === "success") {
        AppToasty.show(t("benchmarkCreationSuccessCaption"), {
          type: "success",
        });
        setShowBenchmarkModal(false);
      } else
        AppToasty.show(t("benchmarkCreationFailedCaption"), {
          type: "danger",
        });
      formRef?.current?.resetForm();
      setShowBenchmarkModal(false);
      setSelectedIndex(null);
    },
    [
      AppToasty,
      t,
      setShowBenchmarkModal.formRef?.current,
      state?.activeWorkspaceID,
    ]
  );

  useEffect(() => {
    if (state?.benchmarks) setDisplayedBenchmarks([...state?.benchmarks]);
  }, [state?.benchmarks && JSON.stringify([...state?.benchmarks])]);

  const benchmarkItemCreationComponent = useMemo(() => {
    return (
      <ToothFairyModal
        modalStyle={{
          maxHeight: isLargeScreen() ? "95%" : 500,
          height: "auto",
          maxWidth: isLargeScreen() ? 700 : 350,
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        key={(Math.floor(Date.now() / 5000) * 5000).toString()}
        isModalOpen={showBenchmarkModal}
        setIsModalOpen={setShowBenchmarkModal}
        modalTitle={
          isEditMode ? "benchmarkEditTitle" : "benchmarkCreationTitle"
        }
        closeModal={() => {
          formRef?.current?.resetForm();
          setShowBenchmarkModal(false);
          setSelectedIndex(null);
          setDisplayTokenSection(false);
        }}
      >
        <BenchmarksSettings
          ref={formRef}
          initialMapValues={initialMapValues}
          onBenchmarkCreated={onBenchmarkCreated}
          isEdit={isEditMode}
          selectedId={displayedBenchmarks[selectedIndex]?.id}
          setShowBenchmarkModal={setShowBenchmarkModal}
          displayTokenSection={displayTokenSection}
          setDisplayTokenSection={setDisplayTokenSection}
          setInitialMapValues={setInitialMapValues}
        />
      </ToothFairyModal>
    );
  }, [
    showBenchmarkModal,
    setShowBenchmarkModal,
    JSON.stringify(initialMapValues),
    setInitialMapValues,
    isEditMode,
    selectedIndex,
    displayedBenchmarks,
    displayTokenSection,
    setDisplayTokenSection,
    displayedBenchmarks[selectedIndex]?.id,
  ]);
  const identifyOptByID = (id) => {
    return state?.benchmarks?.find((u) => u.id === id);
  };
  const handleBenchmarkItemSelection = ({ id, type, item, index }) => {
    setSelectedIndex(index);
    setIsEditMode(true);
    let extendedItem = identifyOptByID(id);
    if (typeof extendedItem?.topics === "string") {
      extendedItem.topics = JSON.parse(extendedItem?.topics);
    }
    setInitialMapValues({
      ...extendedItem,
    });

    setShowBenchmarkModal(true);
  };
  const loadBenchmarks = async (workspaceID) => {
    try {
      setIsLoading(true);
      let resultBenchmarks = await benchmarksHelper?.getBenchmarksByWorkspaceID(
        workspaceID
      );
      resultBenchmarks =
        resultBenchmarks?.data?.BenchmarksByWorkspace?.items?.filter(
          (u) => u?._deleted != true
        );
      dispatch("SET_BENCHMARKS", resultBenchmarks);
    } catch (error) {
      console.error("Error loading benchmarks:", error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (getActiveWorkspace()?.id) {
      loadBenchmarks(getActiveWorkspace()?.id);
    }
  }, [getActiveWorkspace()?.id]);
  const handleBenchmarkItemDeletion = async (item, index) => {
    try {
      setShowChoice(false);
      if (selectedIndex !== null) {
        const _id = displayedBenchmarks[selectedIndex]?.id;
        const result = await benchmarksHelper.deleteBenchmark(_id);
        dispatch("SET_BENCHMARKS", [
          ...state?.benchmarks.filter((u) => u.id !== _id),
        ]);
        AppToasty.show(t("benchmarkDeletionSuccessCaption"), {
          type: "success",
        });
      } else {
        throw new Error("No item selected");
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("benchmarkDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleSearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim() === "") {
      setDisplayedBenchmarks([...state?.benchmarks]);
      return;
    }

    const filteredItems = state?.benchmarks.filter((item) => {
      return (
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
    setDisplayedBenchmarks(filteredItems);
  };
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const handleCreation = () => {
    setInitialMapValues({
      name: "",
      description: "",
    });
    setIsEditMode(false);
    setShowBenchmarkModal(true);
  };
  const isItemActionable = (item) => {
    const _item = item?.item;
    if (_item) return true;
    else return false;
  };
  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={"benchmarkItemCancelConfirmation"}
        caption="benchmarkItemCancelCaption"
        subCaption="benchmarkItemCancelSubCaption"
        confirmCaption={"benchmarkItemDeleteConfirmation"}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleBenchmarkItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem]);

  return (
    <View
      style={{
        minWidth: "100%",
        paddingLeft: 10,
        flex: 1,
        overflow: isLoading && "hidden",
      }}
    >
      {getPlanConfig()?.isBatchJobsEnabled ? (
        <>
          <BenchmarksSettingsToolbar
            handleSearch={handleSearch}
            handleCreation={handleCreation}
          />
          {isLoading ? (
            <ToothFairyLoadingPlaceholder
              backgroundColor={currentTheme?.theme?.softBackgroundColor}
              visible={true}
              containerStyle={{
                backgroundColor: currentTheme?.theme?.transparent,
                minHeight: 250,
              }}
              caption="Loading benchmarks..."
            />
          ) : (
            <>
              {displayedBenchmarks.length > 0 ? (
                <AppTable
                  actionButtons
                  mapIconWithStatus={(item) => {
                    if (item?.status == "active" || item?.status == "syncing")
                      return AppAssets.icons.reload;
                    else return AppAssets.icons.activeModel;
                  }}
                  mainActionLabel="View"
                  showDropDownMenuForActions
                  onClickMainAction={(item, index) => {
                    handleBenchmarkItemSelection({
                      id: item?.id,
                      type: "benchmark",
                      item: item,
                      index: index,
                    });
                  }}
                  actionButtonStyle={{
                    minWidth: 20,
                  }}
                  onActionItemPress={async (id, item, index) => {
                    if (id === "EDIT") {
                      handleBenchmarkItemSelection({
                        id: item?.id,
                        type: "benchmark",
                        item: item,
                        index: index,
                      });
                    } else if (id === "DELETE") {
                      setSelectedItem(item);
                      setSelectedIndex(index);
                      setShowChoice(true);
                    }
                  }}
                  actions={[
                    {
                      label: "Edit",
                      id: "EDIT",
                      icon: AppAssets.icons.edit,
                    },

                    {
                      label: "Delete",
                      id: "DELETE",
                      icon: AppAssets.icons.delete,
                    },
                  ]}
                  isItemActionable={(item) =>
                    isItemActionable({
                      item: item,
                    })
                  }
                  style={{
                    zIndex: -1,
                    height: height - 120,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                  headers={columns}
                  items={displayedBenchmarks
                    ?.slice(
                      (currentPage - 1) * pageSize,
                      (currentPage - 1) * pageSize + pageSize
                    )
                    ?.map((u) => {
                      return {
                        id: u?.id || " ",
                        name: u?.name || " ",
                        description:
                          u?.description || "No description provided",
                        records:
                          typeof u?.questions == "string"
                            ? JSON.parse(u?.questions)?.length?.toString()
                            : u?.questions?.length?.toString(),
                        actions: " ",
                      };
                    })}
                />
              ) : (
                <AppPlaceholderView
                  wrapperStyle={{
                    width: "100%",
                    height: "100%",
                    marginVertical: 20,
                    marginHorizontal: "auto",
                    flex: 1,
                  }}
                  size={30}
                  icon={AppAssets.icons.orbit}
                  mainCaption="EmptyPlaceholder"
                  subCaption="benchmarkSubCaption"
                />
              )}
              {displayedBenchmarks?.length > pageSize && (
                <AppPagination
                  onPageChange={handlePageChange}
                  totalCount={displayedBenchmarks?.length}
                  siblingCount={1}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  wrapperStyle={{
                    marginTop: 10,
                    alignSelf: "flex-end",
                  }}
                />
              )}
            </>
          )}
        </>
      ) : (
        <AppPlaceholderView
          wrapperStyle={{
            width: "100%",
            height: "100%",
            marginVertical: 20,
            marginHorizontal: "auto",
            flex: 1,
          }}
          size={30}
          icon={AppAssets.icons.orbit}
          mainCaption="EmptyPlaceholder"
          subCaption="SettingsSubCaption"
        />
      )}
      {benchmarkItemCreationComponent}
      {_coreChoice}
    </View>
  );
};

export default BenchmarksList;
